// ViewStudentReport.jsx
import React, { useState, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import BackButton from "../components/backButton";
import { TopAdminNav } from "../layouts/TopAdminNav";
import { RegisterCard } from "../components/RegisterCard";
import { useGetStudentReportQuery } from "../hooks/useAdmin";
import { CircleLoader } from "../components/CircleLoader";
import {
  generateAcademicSessions,
  getCurrentAcademicSession,
  getCurrentAcademicTerm,
} from "../utilities/academicSession";
import {
  FaUserGraduate,
  FaAward,
  FaCalendarAlt,
  FaPrint,
  FaGraduationCap,
  FaHeartbeat,
  FaCheckCircle,
  FaSignature,
  FaClipboardList,
  FaFilter,
  FaStar,
  FaInfoCircle,
} from "react-icons/fa";

export const ViewStudentReport = () => {
  const { studentid } = useParams();
  const location = useLocation();
  const { student_name, studentPix } = location.state || {
    student_name: "Student Profile",
    studentPix: "",
  };

  const [selectedTerm, setSelectedTerm] = useState<number | "">(
    getCurrentAcademicTerm(),
  );
  const [selectedSession, setSelectedSession] = useState<string>(
    getCurrentAcademicSession(),
  );
  const sessionOptions = React.useMemo(() => generateAcademicSessions(2025), []);

  const { data: reportQuery, isLoading } = useGetStudentReportQuery(
    studentid ?? "",
    selectedTerm,
    selectedSession,
  );
  const report = reportQuery?.data;
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    window.print();
  };

  const attendanceCount =
    report?.summary?.login_count ??
    report?.summary?.loginCount ??
    report?.attendance_count ??
    0;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-slate-50">
        <CircleLoader />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-slate-50/50 p-6 space-y-6">
        <div className="w-full flex items-center gap-2">
          <BackButton />
          <TopAdminNav name="Admin" title="Report Analytics" />
        </div>
        {/* Filter Controls Bar */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider">
            <FaFilter className="text-[#0096D1]" /> Filter Report:
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={selectedSession}
              onChange={(e) => setSelectedSession(e.target.value)}
              className="text-xs font-bold px-3 py-2 bg-slate-50 text-slate-700 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all cursor-pointer"
            >
              <option value="">All Academic Sessions</option>
              {sessionOptions.map((opt: any) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={selectedTerm}
              onChange={(e) =>
                setSelectedTerm(
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
              className="text-xs font-bold px-3 py-2 bg-slate-50 text-slate-700 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all cursor-pointer"
            >
              <option value="">All Terms</option>
              <option value={1}>1st Term Assessment</option>
              <option value={2}>2nd Term Assessment</option>
              <option value={3}>3rd Term Assessment</option>
            </select>
          </div>
        </div>
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-100 shadow-sm text-slate-400 font-medium italic">
          No verified academic registry record discovered for this entity in the
          selected criteria window.
        </div>
      </div>
    );
  }

  // Helper variables to parse internal structures safely
  const gradesArray = report.grades || [];
  const psychomotorSkills =
    report.psychomotor_skills || report.evaluation?.psychomotor_skills || {};
  const currentSession = report.academic_session || "2025/2026";
  const currentTerm = gradesArray[0]?.grades?.[0]?.term
    ? `${gradesArray[0].grades[0].term === 1 ? "1st" : gradesArray[0].grades[0].term === 2 ? "2nd" : "3rd"} Term`
    : report.term
    ? `${report.term === 1 ? "1st" : report.term === 2 ? "2nd" : "3rd"} Term`
    : "Terminal Review";

  const isObservationalReport =
    gradesArray.length === 0 ||
    report.assessment_mode === "traits_only" ||
    report.assessment_type === "traits_only" ||
    report.evaluation?.assessment_mode === "traits_only" ||
    report.evaluation?.assessment_type === "traits_only" ||
    report.classroom?.assessment_mode === "traits_only" ||
    report.classroom?.assessment_type === "traits_only" ||
    report.student?.classroom?.assessment_mode === "traits_only";

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 space-y-6 print:bg-white print:p-0">
      {/* Upper Navigation Control Layer (Hidden during Document Printing) */}
      <div className="w-full flex items-center justify-between gap-4 print:hidden">
        <div className="flex items-center gap-2 flex-1">
          <BackButton />
          <TopAdminNav
            name="System Viewer"
            title="Academic Transcript Preview"
          />
        </div>
      </div>

      {/* Interactive Filter Bar */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-wrap items-center justify-between gap-4 print:hidden">
        <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider">
          <FaFilter className="text-[#0096D1]" /> Filter Report View:
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={selectedSession}
            onChange={(e) => setSelectedSession(e.target.value)}
            className="text-xs font-bold px-3.5 py-2 bg-slate-50 text-slate-700 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all cursor-pointer"
          >
            <option value="">All Academic Sessions</option>
            {sessionOptions.map((opt: any) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={selectedTerm}
            onChange={(e) =>
              setSelectedTerm(
                e.target.value === "" ? "" : Number(e.target.value),
              )
            }
            className="text-xs font-bold px-3.5 py-2 bg-slate-50 text-slate-700 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all cursor-pointer"
          >
            <option value="">All Terms</option>
            <option value={1}>1st Term Assessment</option>
            <option value={2}>2nd Term Assessment</option>
            <option value={3}>3rd Term Assessment</option>
          </select>
        </div>
      </div>

      {/* Main Report Document Sheet Container */}
      <div ref={printRef} className="space-y-6 print:space-y-4 print:p-4">
        {/* Institutional Academic Heading / Report Master Header Block */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="absolute top-0 right-0 h-32 w-32 bg-[#0096D1]/5 rounded-full blur-2xl pointer-events-none"></div>

          <div className="flex items-center gap-5 w-full md:w-auto">
            <div className="h-20 w-20 rounded-2xl border-2 border-slate-100 overflow-hidden shadow-inner bg-slate-50 shrink-0 flex items-center justify-center text-slate-400 font-black uppercase text-xl">
              {(() => {
                const isValidUrl = (url: any) =>
                  typeof url === "string" &&
                  url.trim() !== "" &&
                  url !== "undefined" &&
                  url !== "null";
                const photoUrl = [
                  studentPix,
                  report?.student?.profile_picture,
                  report?.student?.passport_photo,
                  report?.student?.image,
                  report?.student?.photo,
                  report?.profile_picture,
                  report?.passport_photo,
                ].find(isValidUrl);
                return photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={student_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  student_name?.slice(0, 2)
                );
              })()}
            </div>
            <div className="space-y-1">
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full font-mono text-[10px] bg-emerald-50 text-emerald-600 font-bold border border-emerald-100 uppercase tracking-wider">
                <FaCheckCircle className="text-[9px]" /> Verified Profile
              </span>
              <h1 className="text-xl font-black text-slate-800 tracking-tight leading-none">
                {report?.student?.surname && report?.student?.firstname
                  ? `${report.student.surname} ${report.student.firstname} ${report.student.othername || ""}`.trim()
                  : student_name}
              </h1>
              <p className="text-xs font-semibold text-slate-400 tracking-tight">
                Classroom Allocation:{" "}
                <span className="text-[#012063] font-bold">
                  {gradesArray[0]?.classroom?.name || "—"}
                </span>
              </p>
            </div>
          </div>

          {/* Registry Session Tags Grid */}
          <div className="grid grid-cols-3 gap-3 w-full md:w-auto min-w-[340px]">
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-100 text-left">
              <span className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                <FaCalendarAlt className="text-[#0096D1]" /> Academic Year
              </span>
              <p className="text-sm font-extrabold text-slate-700 font-mono mt-0.5">
                {currentSession}
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-100 text-left">
              <span className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                <FaAward className="text-[#0096D1]" /> Evaluation Slot
              </span>
              <p className="text-sm font-extrabold text-[#0096D1] font-sans mt-0.5">
                {currentTerm}
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-100 text-left">
              <span className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                <FaClipboardList className="text-[#0096D1]" /> Attendance Count
              </span>
              <p className="text-sm font-extrabold text-emerald-600 font-mono mt-0.5">
                {attendanceCount}
              </p>
            </div>
          </div>
        </div>

        {/* Dynamic Structural Assessment Layout: Early Years Observational vs Academic Coursework Matrix */}
        {isObservationalReport ? (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-6">
            {/* Developmental Milestone Header Banner */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl bg-sky-50/70 border border-sky-100 text-left">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-[#0096D1] text-white flex items-center justify-center shrink-0 shadow-xs">
                  <FaStar className="text-base" />
                </div>
                <div>
                  <h2 className="text-sm font-extrabold text-slate-800 tracking-tight">
                    Early Years Developmental & Observational Progress Report
                  </h2>
                  <p className="text-[11px] font-medium text-slate-500">
                    Comprehensive milestone evaluation across affective, cognitive, and psychomotor domains
                  </p>
                </div>
              </div>

              {/* Performance Rating Key Badges */}
              <div className="flex items-center flex-wrap gap-1.5 text-[10px] font-bold">
                <span className="text-slate-400 uppercase tracking-wider mr-1">Rating Key:</span>
                <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">5: Outstanding</span>
                <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200">4: Commendable</span>
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">3: Satisfactory</span>
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">1-2: Emerging</span>
              </div>
            </div>

            {/* Trait & Milestone Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.keys(psychomotorSkills).length > 0 ? (
                Object.entries(psychomotorSkills).map(([trait, rating]: any) => {
                  const score = Number(rating || 0);
                  let tierBadge = "text-slate-600 bg-slate-100 border-slate-200";
                  let tierLabel = "Emerging";
                  if (score === 5) {
                    tierBadge = "text-emerald-700 bg-emerald-50 border-emerald-200";
                    tierLabel = "Outstanding";
                  } else if (score === 4) {
                    tierBadge = "text-blue-700 bg-blue-50 border-blue-200";
                    tierLabel = "Commendable";
                  } else if (score === 3) {
                    tierBadge = "text-amber-700 bg-amber-50 border-amber-200";
                    tierLabel = "Satisfactory";
                  }

                  return (
                    <div
                      key={trait}
                      className="p-4 bg-slate-50/50 rounded-xl border border-slate-100 flex flex-col justify-between gap-3 text-left hover:border-slate-200 transition-all shadow-2xs"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-xs font-bold text-slate-800 capitalize tracking-tight">
                          {trait.replace(/_/g, " ")}
                        </span>
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${tierBadge}`}>
                          {tierLabel} ({score}/5)
                        </span>
                      </div>

                      {/* Visual Stars & Progress Bar */}
                      <div className="space-y-1.5 pt-1">
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <FaStar
                              key={star}
                              className={`text-xs ${
                                star <= score ? "text-amber-400" : "text-slate-200"
                              }`}
                            />
                          ))}
                        </div>
                        <div className="w-full bg-slate-200/60 h-2 rounded-full overflow-hidden flex gap-0.5">
                          {[...Array(5)].map((_, stepIdx) => (
                            <div
                              key={stepIdx}
                              className={`h-full flex-1 transition-all ${
                                stepIdx < score ? "bg-[#0096D1]" : "bg-slate-200"
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="col-span-full py-12 text-center text-slate-400 font-medium text-xs italic">
                  No developmental traits or milestones recorded for this student in this term period.
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
            {/* Left Column Area Block: Core Coursework Results Sheet */}
            <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-[#0096D1]/10 text-[#0096D1] flex items-center justify-center">
                  <FaGraduationCap className="text-base" />
                </div>
                <div>
                  <h2 className="text-[15px] font-extrabold text-slate-800 tracking-tight">
                    Coursework Performance Registry
                  </h2>
                  <p className="text-[11px] font-medium text-slate-400">
                    Terminal compilation of continuous assessment score maps and exams
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto w-full">
                <table className="w-full border-collapse text-left min-w-[580px]">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100 text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                      <th className="py-4 px-6 w-[35%]">
                        Course Module Descriptor
                      </th>
                      <th className="py-4 px-4 w-[20%] text-center">
                        CA Breakdowns
                      </th>
                      <th className="py-4 px-4 w-[15%] text-center">
                        Exam Bound
                      </th>
                      <th className="py-4 px-4 w-[15%] text-center">
                        Total Score
                      </th>
                      <th className="py-4 px-6 w-[15%] text-center">
                        Index Grade
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {gradesArray.length > 0 ? (
                      gradesArray.map((row: any, index: number) => {
                        const subjectTitle =
                          row.subject?.name || "Specified Module";
                        const subGrade = row.grades?.[0] || row;

                        const caTotal = Object.values(
                          subGrade.ca_scores || {},
                        ).reduce(
                          (sum: number, val: any) => sum + Number(val || 0),
                          0,
                        );
                        const examVal = Number(subGrade.exam_score || 0);
                        const computedTotal = caTotal + examVal;

                        let scoreColorBadge =
                          "bg-slate-100 text-slate-600 border-slate-200";
                        if (computedTotal >= 75)
                          scoreColorBadge =
                            "bg-emerald-50 text-emerald-700 border-emerald-100";
                        else if (computedTotal >= 55)
                          scoreColorBadge =
                            "bg-blue-50 text-blue-700 border-blue-100";
                        else if (computedTotal >= 40)
                          scoreColorBadge =
                            "bg-amber-50 text-amber-700 border-amber-100";
                        else if (computedTotal > 0)
                          scoreColorBadge =
                            "bg-rose-50 text-rose-700 border-rose-100";

                        return (
                          <tr
                            key={index}
                            className="bg-white hover:bg-slate-50/20 transition-colors text-[13px] font-semibold text-slate-600"
                          >
                            <td className="py-3.5 px-6 font-bold text-slate-800 capitalize tracking-tight">
                              {subjectTitle}
                              {subGrade.remarks && (
                                <span className="block text-[10px] font-medium text-slate-400 italic mt-0.5 tracking-tight font-sans">
                                  " {subGrade.remarks} "
                                </span>
                              )}
                            </td>
                            <td className="py-3.5 px-4 text-center font-mono text-xs text-slate-500">
                              {subGrade.ca_scores &&
                              Object.keys(subGrade.ca_scores).length > 0 ? (
                                <div className="flex items-center justify-center gap-1.5">
                                  {Object.entries(subGrade.ca_scores).map(
                                    ([key, val]: any) => (
                                      <span
                                        key={key}
                                        className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-bold border border-slate-200"
                                      >
                                        {val}
                                      </span>
                                    ),
                                  )}
                                </div>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                            <td className="py-3.5 px-4 text-center font-mono font-bold text-slate-700">
                              {examVal}
                            </td>
                            <td className="py-3.5 px-4 text-center">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-md font-mono font-black text-xs border ${scoreColorBadge}`}
                              >
                                {computedTotal}
                              </span>
                            </td>
                            <td className="py-3.5 px-6 text-center">
                              <span className="text-sm font-black tracking-wider text-[#012063] font-mono">
                                {subGrade.letter_grade || "—"}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={5}
                          className="py-12 text-center text-slate-400 font-medium text-xs italic"
                        >
                          No terminal subject records populated on this matrix register...
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right Column Area Block: Behavioral Metrics & Affective Areas */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                  <FaHeartbeat className="text-base" />
                </div>
                <div>
                  <h3 className="text-[15px] font-extrabold text-slate-800 tracking-tight">
                    Affective & Psychomotor Index
                  </h3>
                  <p className="text-[11px] font-medium text-slate-400">
                    Validated tracking vectors on a 5-point threshold scale
                  </p>
                </div>
              </div>

              <div className="p-5">
                {Object.keys(psychomotorSkills).length > 0 ? (
                  <div className="space-y-3.5">
                    {Object.entries(psychomotorSkills).map(
                      ([trait, rating]: any) => {
                        const starsCount = Number(rating || 0);
                        return (
                          <div
                            key={trait}
                            className="p-3 bg-slate-50/50 border border-slate-100 rounded-xl space-y-1.5 text-left"
                          >
                            <div className="flex justify-between items-center gap-2">
                              <span className="text-xs font-bold text-slate-700 capitalize tracking-tight">
                                {trait.replace(/_/g, " ")}
                              </span>
                              <span className="text-[11px] font-black text-[#0096D1] font-mono bg-[#0096D1]/5 px-2 py-0.5 rounded">
                                {starsCount}/5
                              </span>
                            </div>
                            <div className="w-full bg-slate-200/60 h-2 rounded-full overflow-hidden flex gap-0.5">
                              {[...Array(5)].map((_, stepIdx) => (
                                <div
                                  key={stepIdx}
                                  className={`h-full flex-1 transition-all ${
                                    stepIdx < starsCount
                                      ? "bg-[#0096D1]"
                                      : "bg-slate-200"
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                ) : (
                  <div className="py-12 text-center text-slate-400 font-medium text-xs italic">
                    No evaluation parameters provided for behavioral vectors...
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Section Block: Appraisals, Verdict Remarks & Validation Sign-Offs */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
          {/* Box Block: Form Teacher's Review Assessment Statement */}
          <div className="p-4 bg-slate-50/60 rounded-xl border border-slate-100 space-y-2 flex flex-col justify-between">
            <div className="space-y-1">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <FaSignature className="text-[#0096D1]" /> Instructor Appraisal
                Verdict
              </h4>
              <p className="text-sm font-semibold text-slate-700 leading-relaxed italic pt-1">
                {report.teacher_remarks || report.evaluation?.teacher_remarks
                  ? `"${report.teacher_remarks || report.evaluation?.teacher_remarks}"`
                  : "No descriptive performance review text logged by the classroom instructor window..."}
              </p>
            </div>
            <div className="pt-4 flex items-center gap-2 text-xs border-t border-slate-200/60 mt-3">
              <div className="h-6 w-6 rounded bg-slate-200 flex items-center justify-center text-[10px] text-slate-400 font-bold">
                FT
              </div>
              <span className="font-bold text-slate-500">
                Class Form Teacher Signature Verified
              </span>
            </div>
          </div>

          {/* Box Block: Head Administrator's Endorsement Core Statement */}
          <div className="p-4 bg-slate-50/60 rounded-xl border border-slate-100 space-y-2 flex flex-col justify-between">
            <div className="space-y-1">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <FaSignature className="text-purple-600" /> Executive
                Directorate Endorsement
              </h4>
              <p className="text-sm font-semibold text-slate-700 leading-relaxed italic pt-1">
                {report.admin_remarks || report.evaluation?.admin_remarks
                  ? `"${report.admin_remarks || report.evaluation?.admin_remarks}"`
                  : "Institutional verification pending final clearance confirmation criteria..."}
              </p>
            </div>
            <div className="pt-4 flex items-center gap-2 text-xs border-t border-slate-200/60 mt-3">
              <div className="h-6 w-6 rounded bg-purple-100 text-purple-600 flex items-center justify-center text-[10px] font-bold">
                ADM
              </div>
              <span className="font-bold text-slate-500">
                School Principal/Director Validation Stamp
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
